import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { Layout } from '@/components/Layout';
import ErrorBoundary from '@/components/ErrorBoundary';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { AuthCallback } from '@/components/AuthCallback';
import LoginPage from '@/pages/LoginPage';
import PublicRecipePage from '@/pages/PublicRecipePage';
import DashboardPage from '@/pages/DashboardPage';
import StaffPage from '@/pages/StaffPage';
import ExpensesPage from '@/pages/ExpensesPage';
import CalendarPage from '@/pages/CalendarPage';
import NotificationsPage from '@/pages/NotificationsPage';
import AIChatPage from '@/pages/AIChatPage';
import SettingsPage from '@/pages/SettingsPage';
import MealPlannerPage from '@/pages/MealPlannerPage';
import HealthHubPage from '@/pages/HealthHubPage';
import KidsZonePage from '@/pages/KidsZonePage';
import KidModePage from '@/pages/KidModePage';
import WeekendPlannerPage from '@/pages/WeekendPlannerPage';
import VisitorLogPage from '@/pages/VisitorLogPage';
import GroceryPage from '@/pages/GroceryPage';
import StaffPortalPage from '@/pages/StaffPortalPage';
import StaffStatementPage from '@/pages/StaffStatementPage';
import ReportsPage from '@/pages/ReportsPage';
import FamilyChatPage from '@/pages/FamilyChatPage';
import EnergyPage from '@/pages/EnergyPage';
import WiFiPage from '@/pages/WiFiPage';
import DocumentsPage from '@/pages/DocumentsPage';
import ProvidersPage from '@/pages/ProvidersPage';
import MaintenancePage from '@/pages/MaintenancePage';
import VehiclesPage from '@/pages/VehiclesPage';
import SmartHomePage from '@/pages/SmartHomePage';
import CamerasPage from '@/pages/CamerasPage';
import InventoryPage from '@/pages/InventoryPage';
import MembersPage from '@/pages/MembersPage';
import LoansPage from '@/pages/LoansPage';
import PersonalExpensesPage from '@/pages/PersonalExpensesPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import AnnouncementsPage from '@/pages/AnnouncementsPage';
import DisplayPage from '@/pages/DisplayPage';
import AuditLogPage from '@/pages/AuditLogPage';
import DevicesPage from '@/pages/DevicesPage';
import StaffAnalyticsPage from '@/pages/StaffAnalyticsPage';
import ReceiptGalleryPage from '@/pages/ReceiptGalleryPage';
import AutomationPage from '@/pages/AutomationPage';
import CashbookPage from '@/pages/CashbookPage';
import SecurityPage from '@/pages/SecurityPage';
import AppliancesPage from '@/pages/AppliancesPage';
import AlertCenterPage from '@/pages/AlertCenterPage';
import ControlCenterPage from '@/pages/ControlCenterPage';
import HomeLayoutPage from '@/pages/HomeLayoutPage';
import RoomAutomationPage from '@/pages/RoomAutomationPage';
import AIIntelligencePage from '@/pages/AIIntelligencePage';
import FaceRecognitionPage from '@/pages/FaceRecognitionPage';
import CookViewPage from '@/pages/CookViewPage';
import PartyRSVPPage from '@/pages/PartyRSVPPage';
import GalleryPage from '@/pages/GalleryPage';
import PublicTimelinePage from '@/pages/PublicTimelinePage';
import KioskPage from '@/pages/KioskPage';
import CheckinPage from '@/pages/CheckinPage';
import WelcomeWallPage from '@/pages/WelcomeWallPage';
import PublicMoneySheetPage from '@/pages/PublicMoneySheetPage';
import MyPhotosPage from '@/pages/MyPhotosPage';
import SharedRSVPViewPage from '@/pages/SharedRSVPViewPage';
import PackingLabelPage from '@/pages/PackingLabelPage';
import InvitePage from '@/pages/InvitePage';
import EventManagerPage from '@/pages/EventManagerPage';
import TripsPage from '@/pages/TripsPage';
import { BrandingProvider } from '@/contexts/BrandingContext';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0D0D0D]">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // ErrorBoundary keyed on pathname → automatically resets when the user
  // navigates to a different page (otherwise a sticky crash on /meals would
  // persist when they navigate to /dashboard).
  return (
    <Layout>
      <PWAInstallPrompt />
      <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
    </Layout>
  );
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/r/:dishKey" element={<PublicRecipePage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute><StaffPage /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/ai-chat" element={<ProtectedRoute><AIChatPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/meals" element={<ProtectedRoute><MealPlannerPage /></ProtectedRoute>} />
      <Route path="/health" element={<ProtectedRoute><HealthHubPage /></ProtectedRoute>} />
      <Route path="/kids" element={<ProtectedRoute><KidsZonePage /></ProtectedRoute>} />
      <Route path="/weekend" element={<ProtectedRoute><WeekendPlannerPage /></ProtectedRoute>} />
      <Route path="/kid-mode" element={<KidModePage />} />
      <Route path="/visitors" element={<ProtectedRoute><VisitorLogPage /></ProtectedRoute>} />
      <Route path="/grocery" element={<ProtectedRoute><GroceryPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/family-chat" element={<ProtectedRoute><FamilyChatPage /></ProtectedRoute>} />
      <Route path="/energy" element={<ProtectedRoute><EnergyPage /></ProtectedRoute>} />
      <Route path="/wifi" element={<ProtectedRoute><WiFiPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
      <Route path="/providers" element={<ProtectedRoute><ProvidersPage /></ProtectedRoute>} />
      <Route path="/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
      <Route path="/vehicles" element={<ProtectedRoute><VehiclesPage /></ProtectedRoute>} />
      <Route path="/smart-home" element={<ProtectedRoute><SmartHomePage /></ProtectedRoute>} />
      <Route path="/cameras" element={<ProtectedRoute><CamerasPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><MembersPage /></ProtectedRoute>} />
      <Route path="/loans" element={<ProtectedRoute><LoansPage /></ProtectedRoute>} />
      <Route path="/personal-expenses" element={<ProtectedRoute><PersonalExpensesPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/announcements" element={<ProtectedRoute><AnnouncementsPage /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><DevicesPage /></ProtectedRoute>} />
      <Route path="/staff-analytics" element={<ProtectedRoute><StaffAnalyticsPage /></ProtectedRoute>} />
      <Route path="/receipts" element={<ProtectedRoute><ReceiptGalleryPage /></ProtectedRoute>} />
      <Route path="/automation" element={<ProtectedRoute><AutomationPage /></ProtectedRoute>} />
      <Route path="/cashbook" element={<ProtectedRoute><CashbookPage /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
      <Route path="/appliances" element={<ProtectedRoute><AppliancesPage /></ProtectedRoute>} />
      <Route path="/alert-center" element={<ProtectedRoute><AlertCenterPage /></ProtectedRoute>} />
      <Route path="/control-center" element={<ProtectedRoute><ControlCenterPage /></ProtectedRoute>} />
      <Route path="/home-layout" element={<ProtectedRoute><HomeLayoutPage /></ProtectedRoute>} />
      <Route path="/room-automation" element={<ProtectedRoute><RoomAutomationPage /></ProtectedRoute>} />
      <Route path="/ai-intelligence" element={<ProtectedRoute><AIIntelligencePage /></ProtectedRoute>} />
      <Route path="/face-recognition" element={<ProtectedRoute><FaceRecognitionPage /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventManagerPage /></ProtectedRoute>} />
      <Route path="/trips" element={<ProtectedRoute><TripsPage /></ProtectedRoute>} />
      <Route path="/display" element={<DisplayPage />} />
      <Route path="/staff-portal" element={<StaffPortalPage />} />
      <Route path="/staff-statement/:staffId" element={<StaffStatementPage />} />
      <Route path="/cook-view" element={<CookViewPage />} />
      <Route path="/party/:slug" element={<PartyRSVPPage />} />
      <Route path="/party/:slug/gallery" element={<GalleryPage />} />
      <Route path="/party/:slug/timeline" element={<PublicTimelinePage />} />
      <Route path="/party/:slug/kiosk" element={<KioskPage />} />
      <Route path="/party/:slug/welcome-wall" element={<WelcomeWallPage />} />
      <Route path="/party/:slug/checkin/:token" element={<CheckinPage />} />
      <Route path="/party-sheet/:token" element={<PublicMoneySheetPage />} />
      <Route path="/party/:slug/my-photos/:token" element={<MyPhotosPage />} />
      <Route path="/events/rsvps/:token" element={<SharedRSVPViewPage />} />
      <Route path="/events/:event_id/labels/:label_id" element={<PackingLabelPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrandingProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          </BrandingProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
